import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faGlobe, faStream, faExchangeAlt, faRedo, faCrosshairs,
  faSpider, faShieldAlt, faPlug, faArrowLeft, faCog,
  faUpload, faUserShield, faNetworkWired, faBug, faRobot,
  faClipboardCheck, faBolt, faHistory, faChartBar, faSyncAlt,
  faWindowRestore, faPlay, faStop
} from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import webTargetService from '../services/webTargetService';
import proxyService from '../services/proxyService';
import exposiqService from '../services/exposiqService';
import httpExchangeService from '../services/httpExchangeService';
import SiteMapTree from '../components/webtesting/SiteMapTree';
import ExchangeTable from '../components/webtesting/ExchangeTable';
import RequestViewer from '../components/webtesting/RequestViewer';
import ResponseViewer from '../components/webtesting/ResponseViewer';
import HttpSearchBar from '../components/webtesting/HttpSearchBar';
import { buildSearchRegex } from '../components/webtesting/HighlightedText';
import RepeaterTab from '../components/webtesting/RepeaterTab';
import IntruderTab from '../components/webtesting/IntruderTab';
import SpiderPanel from '../components/webtesting/SpiderPanel';
import ProxyControlPanel from '../components/webtesting/ProxyControlPanel';
import AuthProfileManager from '../components/webtesting/AuthProfileManager';
import WebSocketTab from '../components/webtesting/WebSocketTab';
import ImportDialog from '../components/webtesting/ImportDialog';
import AuthSmashTab from '../components/webtesting/AuthSmashTab';
import WebFindingsList from '../components/webtesting/WebFindingsList';
import Scanner from '../components/webtesting/Scanner';
import ExposIQScanTab from '../components/webtesting/ExposIQScanTab';
import ExposIQCoverageOverlay from '../components/webtesting/ExposIQCoverageOverlay';
import WstgChecklist from '../components/webtesting/WstgChecklist';
import OASTPanel from '../components/webtesting/OASTPanel';
import BlueTeamScenarioBuilder from '../components/webtesting/BlueTeamScenarioBuilder';
import RegressionTestPanel from '../components/webtesting/RegressionTestPanel';
import WebTestReports from '../components/webtesting/WebTestReports';
import WebTargetSettingsModal from '../components/webtesting/WebTargetSettingsModal';
import GlobalAuthProfileNotifications from '../components/webtesting/GlobalAuthProfileNotifications';
import VwgAuthzTester from '../components/webtesting/VwgAuthzTester';

const TABS = [
  { id: 'traffic', label: 'Traffic', icon: faStream },
  { id: 'repeater', label: 'Repeater', icon: faRedo },
  { id: 'intruder', label: 'Intruder', icon: faCrosshairs },
  { id: 'spider', label: 'Spider', icon: faSpider },
  { id: 'websocket', label: 'WebSocket', icon: faPlug },
  { id: 'auth', label: 'Profiles', icon: faUserShield },
  { id: 'authz', label: 'AuthSmash', icon: faShieldAlt },
  { id: 'vwg-authz', label: 'VWG AuthZ', icon: faWindowRestore },
  { id: 'findings', label: 'Findings', icon: faBug },
  { id: 'scanner', label: 'Scanner', icon: faBolt },
  { id: 'exposiq', label: 'ExposIQ', icon: faRobot },
  { id: 'wstg', label: 'WSTG', icon: faClipboardCheck },
  { id: 'oast', label: 'OAST', icon: faNetworkWired },
  { id: 'blueteam', label: 'Blue Team', icon: faShieldAlt },
  { id: 'regression', label: 'Regression', icon: faHistory },
  { id: 'reports', label: 'Reports', icon: faChartBar },
  { id: 'proxy', label: 'Proxy', icon: faPlug },
];

const WebTestingPage = () => {
  const { webTargetId } = useParams();
  const navigate = useNavigate();
  const { darkMode: isDarkMode } = useTheme();
  const { t } = useLanguage();

  const [webTarget, setWebTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('traffic');
  const [selectedExchange, setSelectedExchange] = useState(null);
  const [selectedTreeFilter, setSelectedTreeFilter] = useState(null);
  const [treeExternalFilter, setTreeExternalFilter] = useState(null); // Traffic filters applied to the tree
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [restartingProxy, setRestartingProxy] = useState(false);

  // Smart proxy control: start/restart depending on proxy state.
  const handleProxyToggle = async () => {
    if (!webTarget?._id) return;
    setRestartingProxy(true);
    try {
      const sessions = await proxyService.getAll({ webTargetId: webTarget._id });
      const active = (sessions || []).find(s => s.status === 'running');
      if (active) {
        const result = await proxyService.restart(active._id);
        toast.success(`Proxy restarted${result?.port ? ` (port ${result.port})` : ''}.`);
      } else {
        // Find the most recent stopped session and re-start it, or create a new one
        const stopped = (sessions || []).find(s => s.status === 'stopped');
        if (stopped) {
          await proxyService.start(stopped._id);
          toast.success('Proxy started.');
        } else {
          // No previous session — create one with defaults
          const session = await proxyService.create({
            webTargetId: webTarget._id,
            projectId: webTarget.project,
            name: `Proxy ${new Date().toLocaleTimeString()}`,
            proxyConfig: { listenPort: 8080, listenAddress: '0.0.0.0' }
          });
          await proxyService.start(session._id);
          toast.success('Proxy started on port 8080.');
        }
        setProxyRunning(true);
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message;
      toast.error(`Proxy ${proxyRunning ? 'restart' : 'start'} failed: ${msg}`);
    } finally {
      setRestartingProxy(false);
    }
  };

  const handleStopProxy = async () => {
    if (!webTarget?._id) return;
    setRestartingProxy(true);
    try {
      const sessions = await proxyService.getAll({ webTargetId: webTarget._id });
      const active = (sessions || []).find(s => s.status === 'running');
      if (active) {
        await proxyService.stop(active._id);
        toast.success('Proxy stopped.');
        setProxyRunning(false);
      }
    } catch (err) {
      toast.error('Proxy stop failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setRestartingProxy(false);
    }
  };
  const [sidebarWidth, setSidebarWidth] = useState(280);
  const [proxyRunning, setProxyRunning] = useState(false);
  const [proxySessionId, setProxySessionId] = useState(null);
  const [reqResPanelHeight, setReqResPanelHeight] = useState(40); // Percentage
  const [isDragging, setIsDragging] = useState(false);

  // Search state
  const [searchVisible, setSearchVisible] = useState(false);
  const [searchBarKey, setSearchBarKey] = useState(0); // bump to clear the always-visible search bar
  const [searchRegex, setSearchRegex] = useState(null);
  const [activeMatchIndex, setActiveMatchIndex] = useState(0);
  const [reqMatchCount, setReqMatchCount] = useState(0);
  const [resMatchCount, setResMatchCount] = useState(0);
  const totalMatches = reqMatchCount + resMatchCount;

  const handleSearchChange = useCallback((query, options) => {
    const regex = buildSearchRegex(query, options);
    setSearchRegex(regex);
    setActiveMatchIndex(0);
  }, []);

  const handleSearchNext = useCallback(() => {
    if (totalMatches === 0) return;
    setActiveMatchIndex(prev => (prev + 1) % totalMatches);
  }, [totalMatches]);

  const handleSearchPrev = useCallback(() => {
    if (totalMatches === 0) return;
    setActiveMatchIndex(prev => (prev - 1 + totalMatches) % totalMatches);
  }, [totalMatches]);

  const handleSearchClose = useCallback(() => {
    // The Traffic search bar is always visible — "close" clears the query/highlights.
    setSearchRegex(null);
    setActiveMatchIndex(0);
    setSearchBarKey(k => k + 1); // remount to reset the input field
  }, []);

  // Draggable divider handlers
  const handleDividerMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (e) => {
      const container = document.querySelector('[data-traffic-container]');
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const mouseY = e.clientY - containerRect.top;
      const containerHeight = containerRect.height;

      // Calculate percentage from bottom (Request/Response panel height)
      const newPanelHeight = ((containerHeight - mouseY) / containerHeight) * 100;

      // Constrain between 20% and 80%
      const constrainedHeight = Math.max(20, Math.min(80, newPanelHeight));
      setReqResPanelHeight(constrainedHeight);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Ctrl+F to toggle search
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f' && selectedExchange && activeTab === 'traffic') {
        e.preventDefault();
        setSearchVisible(v => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [selectedExchange, activeTab]);

  // Reset search when exchange changes
  useEffect(() => {
    setActiveMatchIndex(0);
  }, [selectedExchange?._id]);

  // Highlight colors — persisted in localStorage
  const [highlightColors, setHighlightColors] = useState(() => {
    try {
      const saved = localStorage.getItem(`purpleteam-highlights-${webTargetId}`);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  const handleHighlightChange = useCallback((key, color) => {
    setHighlightColors(prev => {
      const next = { ...prev };
      if (color) next[key] = color;
      else delete next[key];
      localStorage.setItem(`purpleteam-highlights-${webTargetId}`, JSON.stringify(next));
      return next;
    });
  }, [webTargetId]);

  // Per-exchange highlight colors — keyed by exchange _id (marks ONE row, not every
  // row for the same URL). The tree keeps its own URL-keyed highlightColors above.
  const [exchangeColors, setExchangeColors] = useState(() => {
    try {
      const saved = localStorage.getItem(`purpleteam-ex-highlights-${webTargetId}`);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });

  const handleExchangeColorChange = useCallback((id, color) => {
    if (!id) return;
    setExchangeColors(prev => {
      const next = { ...prev };
      if (color) next[id] = color;
      else delete next[id];
      localStorage.setItem(`purpleteam-ex-highlights-${webTargetId}`, JSON.stringify(next));
      return next;
    });
  }, [webTargetId]);

  // Ref to reload exchange table when tree deletes items
  const [exchangeReloadTrigger, setExchangeReloadTrigger] = useState(0);
  const handleTreeDeletePath = useCallback(() => {
    setExchangeReloadTrigger(p => p + 1);
  }, []);

  const loadTarget = useCallback(async () => {
    try {
      setLoading(true);
      const data = await webTargetService.getById(webTargetId);
      setWebTarget(data);
    } catch (err) {
      console.error('Error loading web target:', err);
    } finally {
      setLoading(false);
    }
  }, [webTargetId]);

  useEffect(() => {
    loadTarget();
  }, [loadTarget]);

  // Detect proxy running state
  useEffect(() => {
    if (!webTargetId) return;
    let interval;
    const checkProxy = async () => {
      try {
        const sessions = await proxyService.getAll({ webTargetId });
        const running = sessions.find(s => s.status === 'running');
        setProxyRunning(!!running);
        setProxySessionId(running?._id || null);
      } catch {}
    };
    checkProxy();
    interval = setInterval(checkProxy, 5000);
    return () => clearInterval(interval);
  }, [webTargetId]);

  const handleExchangeSelect = (exchange) => {
    setSelectedExchange(exchange);
  };

  // The traffic list row is slim (no request headers/body). Fetch the FULL
  // exchange so Repeater/Intruder get the real captured request (incl. X-UserToken).
  const fetchFullExchange = async (exchange) => {
    if (!exchange?._id) return exchange;
    try {
      const full = await httpExchangeService.getById(exchange._id);
      // Preserve capturing-profile hint so the tab can preselect the right profile.
      if (exchange.capturedProfiles && !full.capturedProfiles) full.capturedProfiles = exchange.capturedProfiles;
      return full;
    } catch {
      return exchange;
    }
  };

  const handleSendToRepeater = async (exchange) => {
    setSelectedExchange(await fetchFullExchange(exchange));
    setActiveTab('repeater');
  };

  const handleSendToIntruder = async (exchange) => {
    setSelectedExchange(await fetchFullExchange(exchange));
    setActiveTab('intruder');
  };

  // State for AuthSmash URL selection
  const [authSmashSelectedUrls, setAuthSmashSelectedUrls] = useState([]);

  const handleSendToAuthSmash = useCallback((urls) => {
    // URLs format: array of strings like ["https://example.com/path1", "https://example.com/path2"]
    setAuthSmashSelectedUrls(urls);
    setActiveTab('authz');
  }, []);

  // ExposIQ scan integration
  const [activeScanHosts, setActiveScanHosts] = useState({});

  const handleScanWithExposiq = useCallback(async (host, profile) => {
    if (!webTarget) return;
    try {
      const result = await exposiqService.triggerScan(webTarget._id, { scanProfile: profile, host });
      // Track this host as actively scanning
      setActiveScanHosts(prev => ({
        ...prev,
        [host]: { scanId: result.exposiqScanId, progress: 0, status: 'running' }
      }));
      // Switch to ExposIQ tab
      setActiveTab('exposiq');
      // Start polling progress
      const pollInterval = setInterval(async () => {
        try {
          const status = await exposiqService.pollScanStatus(result.exposiqScanId);
          setActiveScanHosts(prev => ({
            ...prev,
            [host]: { scanId: result.exposiqScanId, progress: status.progress || 0, status: status.status }
          }));
          if (['completed', 'failed', 'stopped', 'cancelled'].includes(status.status)) {
            clearInterval(pollInterval);
            // Remove from active hosts after a short delay
            setTimeout(() => {
              setActiveScanHosts(prev => {
                const next = { ...prev };
                delete next[host];
                return next;
              });
            }, 5000);
            loadTarget(); // Refresh web target to get updated activeScan
          }
        } catch {
          clearInterval(pollInterval);
        }
      }, 5000);
    } catch (err) {
      console.error('Failed to trigger ExposIQ scan:', err);
    }
  }, [webTarget, loadTarget]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <FontAwesomeIcon icon={faGlobe} spin className="text-3xl text-blue-500" />
      </div>
    );
  }

  if (!webTarget) {
    return (
      <div className={`p-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
        Web target not found.
      </div>
    );
  }

  return (
    <div className={`h-[calc(100vh-64px)] flex flex-col ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Global notifications - polls in background and shows toasts */}
      <GlobalAuthProfileNotifications webTargetId={webTargetId} />

      {/* Header bar */}
      <div className={`flex items-center justify-between px-4 py-2 border-b ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className={`p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </button>
          <FontAwesomeIcon icon={faGlobe} className="text-teal-500" />
          <div>
            <h1 className={`text-sm font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {webTarget.baseUrl}
            </h1>
            <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              {webTarget.webType} - {webTarget.siteMap?.length || 0} endpoints
              {webTarget.wafDetected && ` - WAF: ${webTarget.wafDetected}`}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowImportDialog(true)}
            className={`px-3 py-1.5 text-xs rounded flex items-center gap-1.5 ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            <FontAwesomeIcon icon={faUpload} />
            Import
          </button>
          <button
            onClick={() => setShowSettingsModal(true)}
            className={`px-3 py-1.5 text-xs rounded flex items-center gap-1.5 ${isDarkMode ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            <FontAwesomeIcon icon={faCog} />
            Settings
          </button>
          {proxyRunning ? (
            <>
              <button
                onClick={handleProxyToggle}
                disabled={restartingProxy}
                title="Restart the running proxy"
                className="px-3 py-1.5 text-xs rounded-l flex items-center gap-1.5 bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50"
              >
                <FontAwesomeIcon icon={faSyncAlt} spin={restartingProxy} />
                Restart Proxy
              </button>
              <button
                onClick={handleStopProxy}
                disabled={restartingProxy}
                title="Stop the proxy"
                className="px-3 py-1.5 text-xs rounded-r flex items-center gap-1.5 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 border-l border-red-700"
              >
                <FontAwesomeIcon icon={faStop} />
              </button>
            </>
          ) : (
            <button
              onClick={handleProxyToggle}
              disabled={restartingProxy}
              title="Start proxy for this target"
              className="px-3 py-1.5 text-xs rounded flex items-center gap-1.5 bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faPlay} spin={restartingProxy} />
              Start Proxy
            </button>
          )}
        </div>
      </div>

      {/* Main content area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left sidebar - Site Map Tree */}
        <div
          className={`border-r overflow-y-auto ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}
          style={{ width: sidebarWidth, minWidth: 200, maxWidth: 400 }}
        >
          <SiteMapTree
            externalFilter={treeExternalFilter}
            webTargetId={webTargetId}
            projectId={webTarget?.project}
            isDarkMode={isDarkMode}
            onSelect={setSelectedTreeFilter}
            selectedFilter={selectedTreeFilter}
            proxyRunning={proxyRunning}
            proxySessionId={proxySessionId}
            onSendToRepeater={handleSendToRepeater}
            onSendToIntruder={handleSendToIntruder}
            onSendToAuthSmash={handleSendToAuthSmash}
            highlightColors={highlightColors}
            onHighlightChange={handleHighlightChange}
            onDeletePath={handleTreeDeletePath}
            onTabChange={setActiveTab}
            onScanWithExposiq={handleScanWithExposiq}
            activeScanHosts={activeScanHosts}
          />
        </div>

        {/* Right content area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Tab bar */}
          <div className={`flex flex-wrap border-b ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? (isDarkMode ? 'border-teal-400 text-teal-400' : 'border-teal-500 text-teal-600')
                    : (isDarkMode ? 'border-transparent text-gray-500 hover:text-gray-300' : 'border-transparent text-gray-500 hover:text-gray-700')
                }`}
              >
                <FontAwesomeIcon icon={tab.icon} />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-hidden">
            {activeTab === 'traffic' && (
              <div className="flex flex-col h-full" data-traffic-container>
                <div className="overflow-auto" style={{ height: selectedExchange ? `${100 - reqResPanelHeight}%` : '100%' }}>
                  <ExchangeTable
                    webTargetId={webTargetId}
                    projectId={webTarget?.project}
                    isDarkMode={isDarkMode}
                    onSelect={handleExchangeSelect}
                    onSendToRepeater={handleSendToRepeater}
                    onSendToIntruder={handleSendToIntruder}
                    onSendToAuthSmash={handleSendToAuthSmash}
                    onTreeFilterChange={setTreeExternalFilter}
                    filterHost={selectedTreeFilter?.host}
                    filterPath={selectedTreeFilter?.path}
                    proxyRunning={proxyRunning}
                    highlightColors={highlightColors}
                    onHighlightChange={handleHighlightChange}
                    exchangeColors={exchangeColors}
                    onExchangeColorChange={handleExchangeColorChange}
                    onTabChange={setActiveTab}
                    key={exchangeReloadTrigger}
                  />
                </div>
                {selectedExchange && (
                  <>
                    {/* Draggable divider */}
                    <div
                      className={`h-1 cursor-ns-resize hover:bg-teal-500 transition-colors ${isDarkMode ? 'bg-gray-700' : 'bg-gray-300'} ${isDragging ? 'bg-teal-500' : ''}`}
                      onMouseDown={handleDividerMouseDown}
                      style={{ flexShrink: 0 }}
                    />
                    <div className="flex flex-col" style={{ height: `${reqResPanelHeight}%` }}>
                      <HttpSearchBar
                        key={searchBarKey}
                        isDarkMode={isDarkMode}
                        visible={true}
                        onSearchChange={handleSearchChange}
                        totalMatches={totalMatches}
                        activeMatchIndex={activeMatchIndex}
                        onNext={handleSearchNext}
                        onPrev={handleSearchPrev}
                        onClose={handleSearchClose}
                      />
                      <div className="flex flex-1 min-h-0">
                        <div className={`flex-1 overflow-auto ${isDarkMode ? 'border-r border-gray-700' : 'border-r border-gray-200'}`}>
                          <RequestViewer
                            exchange={selectedExchange}
                            isDarkMode={isDarkMode}
                            searchRegex={searchRegex}
                            activeMatchIndex={activeMatchIndex}
                            matchOffset={0}
                            onMatchCount={setReqMatchCount}
                          />
                        </div>
                        <div className="flex-1 overflow-auto">
                          <ResponseViewer
                            exchange={selectedExchange}
                            isDarkMode={isDarkMode}
                            searchRegex={searchRegex}
                            activeMatchIndex={activeMatchIndex}
                            matchOffset={reqMatchCount}
                            onMatchCount={setResMatchCount}
                          />
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {activeTab === 'repeater' && (
              <RepeaterTab
                webTarget={webTarget}
                initialExchange={selectedExchange}
                isDarkMode={isDarkMode}
              />
            )}

            {activeTab === 'intruder' && (
              <IntruderTab
                webTarget={webTarget}
                initialExchange={selectedExchange}
                isDarkMode={isDarkMode}
                onSendToRepeater={handleSendToRepeater}
                onSendToIntruder={handleSendToIntruder}
              />
            )}

            {activeTab === 'spider' && (
              <SpiderPanel
                webTarget={webTarget}
                isDarkMode={isDarkMode}
                onRefresh={loadTarget}
              />
            )}

            {activeTab === 'auth' && (
              <AuthProfileManager
                webTarget={webTarget}
                isDarkMode={isDarkMode}
              />
            )}

            {activeTab === 'websocket' && (
              <WebSocketTab
                webTarget={webTarget}
                isDarkMode={isDarkMode}
              />
            )}

            {activeTab === 'authz' && (
              <AuthSmashTab
                webTarget={webTarget}
                isDarkMode={isDarkMode}
                initialSelectedUrls={authSmashSelectedUrls}
                onUrlsConsumed={() => setAuthSmashSelectedUrls([])}
                onManualUrlsChange={(manualUrls, excludedUrls) =>
                  setWebTarget(prev => prev ? { ...prev, manualUrls, excludedUrls } : prev)}
                onSendToRepeater={(exchangeId) => handleSendToRepeater({ _id: exchangeId })}
                onSendToIntruder={(exchangeId) => handleSendToIntruder({ _id: exchangeId })}
              />
            )}

            {activeTab === 'vwg-authz' && (
              <VwgAuthzTester
                webTarget={webTarget}
                isDarkMode={isDarkMode}
              />
            )}

            {activeTab === 'findings' && (
              <WebFindingsList
                webTarget={webTarget}
                isDarkMode={isDarkMode}
              />
            )}

            {activeTab === 'scanner' && (
              <Scanner
                webTargetId={webTarget?._id}
                projectId={webTarget?.project}
                isDarkMode={isDarkMode}
              />
            )}

            {activeTab === 'exposiq' && (
              <div className="flex h-full">
                <div className="flex-1 overflow-hidden">
                  <ExposIQScanTab
                    webTarget={webTarget}
                    isDarkMode={isDarkMode}
                    onRefresh={loadTarget}
                  />
                </div>
                <div className={`w-80 border-l overflow-hidden ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <ExposIQCoverageOverlay
                    webTarget={webTarget}
                    isDarkMode={isDarkMode}
                  />
                </div>
              </div>
            )}

            {activeTab === 'wstg' && (
              <WstgChecklist
                webTarget={webTarget}
                isDarkMode={isDarkMode}
              />
            )}

            {activeTab === 'oast' && (
              <OASTPanel
                webTarget={webTarget}
                isDarkMode={isDarkMode}
              />
            )}

            {activeTab === 'blueteam' && (
              <BlueTeamScenarioBuilder
                webTarget={webTarget}
                isDarkMode={isDarkMode}
              />
            )}

            {activeTab === 'regression' && (
              <RegressionTestPanel
                webTarget={webTarget}
                isDarkMode={isDarkMode}
              />
            )}

            {activeTab === 'reports' && (
              <WebTestReports
                webTarget={webTarget}
                isDarkMode={isDarkMode}
              />
            )}

            {activeTab === 'proxy' && (
              <ProxyControlPanel
                webTarget={webTarget}
                isDarkMode={isDarkMode}
                onRefresh={loadTarget}
              />
            )}
          </div>
        </div>
      </div>

      {/* Import Dialog */}
      {showImportDialog && (
        <ImportDialog
          webTarget={webTarget}
          isDarkMode={isDarkMode}
          onClose={() => setShowImportDialog(false)}
          onImported={() => { setShowImportDialog(false); loadTarget(); }}
        />
      )}

      {/* Settings Modal */}
      {showSettingsModal && (
        <WebTargetSettingsModal
          webTarget={webTarget}
          isDarkMode={isDarkMode}
          onClose={() => setShowSettingsModal(false)}
          onSave={(updatedSettings) => {
            // Refresh web target data
            loadTarget();
            setShowSettingsModal(false);
          }}
        />
      )}
    </div>
  );
};

export default WebTestingPage;
